import React, { useEffect, useState, useRef, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  Heart,
  Lock,
  Package,
  Search,
  ShoppingBag,
  User,
} from "lucide-react";

import {
  get_cart_products,
  get_wishlist_products,
} from "../store/reducers/cardReducer";
import SearchDropdown from "./SearchDropdown";
import LocationPicker from "./LocationPicker";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { useCurrency } from "../hooks/useCurrency";
import { getCurrencySymbol, SUPPORTED_CURRENCIES } from "../utils/currencyUtils";
import { Input } from "../components/ui/input";

const Headers = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { userInfo, profileImage } = useSelector((state) => state.auth);
  const { card_product_count, wishlist_count } = useSelector(
    (state) => state.card
  );

  const { currencyCode, setCurrencyCode } = useCurrency();

  const [searchValue, setSearchValue] = useState("");
  const [category, setCategory] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const desktopSearchRef = useRef(null);
  const mobileSearchRef = useRef(null);

  const search = () => {
    setShowDropdown(false);
    navigate(`/products/search?category=${category}&value=${searchValue}`);
  };

  const handleSearchFocus = useCallback(() => setShowDropdown(true), []);
  const handleDropdownClose = useCallback(() => setShowDropdown(false), []);

  // Close dropdown on click outside of search containers
  useEffect(() => {
    const handler = (e) => {
      const inDesktop = desktopSearchRef.current?.contains(e.target);
      const inMobile = mobileSearchRef.current?.contains(e.target);
      if (!inDesktop && !inMobile) {
        setShowDropdown(false);
      }
    };
    if (showDropdown) {
      document.addEventListener('mousedown', handler);
    }
    return () => document.removeEventListener('mousedown', handler);
  }, [showDropdown]);

  const redirect_cart_page = () => {
    navigate(userInfo ? `/cart` : `/login`);
  };

  useEffect(() => {
    if (userInfo) {
      dispatch(get_cart_products(userInfo.id));
      dispatch(get_wishlist_products(userInfo.id));
    }
  }, [userInfo]);

  return (
    <div className="w-full bg-white border-b border-gray-200 relative z-[50]">
      <div className="w-[92%] max-w-7xl mx-auto py-3 md-lg:py-2.5">

        {/* ── Row 1 ── */}
        <div className="flex items-center gap-5 md-lg:gap-3">

          {/* Logo + tagline */}
          <div className="flex flex-col items-start cursor-pointer shrink-0" onClick={() => navigate("/")}>
            <img
              className="w-[60px] h-auto"
              src={`${process.env.REACT_APP_URL}/images/omahlogoupdated.png`}
              alt="logo"
            />
            <span className="text-[10px] text-gray-400 tracking-wide leading-tight mt-0.5 md-lg:hidden">
              Connecting dreams to doorsteps.
            </span>
          </div>

          {/* Desktop search bar — hidden on mobile */}
          <div className="relative flex-1 min-w-0 md-lg:hidden" ref={desktopSearchRef}>
            <div className={`flex items-center w-full border rounded-lg h-[44px] bg-white shadow-[inset_0_1px_2px_rgba(0,0,0,0.04)] transition-colors ${
              showDropdown ? 'border-green-400 ring-1 ring-green-100' : 'border-gray-300'
            }`}>
              <LocationPicker
                triggerClassName="h-full w-[148px] pl-3 pr-2 bg-gray-50/80 rounded-l-lg rounded-r-none hover:bg-gray-100/80"
              />
              <div className="w-px h-6 bg-gray-200 shrink-0" />
              <Input
                className="border-none shadow-none focus-visible:ring-0 focus-visible:border-transparent w-full text-sm bg-transparent px-3 h-full"
                onChange={(e) => setSearchValue(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && search()}
                onFocus={handleSearchFocus}
                type="text"
                placeholder="Search products, brands and categories"
                value={searchValue}
              />
              <button
                onClick={search}
                className="h-full px-4 bg-[#00A651] hover:bg-[#009245] text-white shrink-0 transition-colors flex items-center rounded-r-lg"
              >
                <Search size={18} />
              </button>
            </div>
            <SearchDropdown
              visible={showDropdown}
              onClose={handleDropdownClose}
              searchValue={searchValue}
            />
          </div>

          {/* Right actions */}
          <div className="flex items-center gap-2.5 md-lg:gap-2 shrink-0 md-lg:ml-auto">
            {/* Sell — desktop only */}
            <Link to={process.env.REACT_APP_SELLER_DASHBOARD_URL} className="flex md-lg:hidden">
              <button className="flex items-center gap-1.5 bg-[#00A651] hover:bg-[#009245] text-white font-bold px-5 h-[40px] rounded-lg text-sm transition-colors shadow-sm">
                Sell <Package size={15} />
              </button>
            </Link>

            <div className="w-px h-5 bg-gray-200 md-lg:hidden" />

            {/* Currency */}
            <Select value={currencyCode} onValueChange={setCurrencyCode}>
              <SelectTrigger className="h-[34px] md-lg:h-[30px] text-xs font-semibold border-gray-200 rounded-lg px-2.5 md-lg:px-2 w-auto gap-1">
                <span className="text-xs font-semibold">{currencyCode}</span>
              </SelectTrigger>
              <SelectContent>
                {SUPPORTED_CURRENCIES.map((code) => (
                  <SelectItem key={code} value={code} className="text-xs cursor-pointer">
                    {getCurrencySymbol(code)} {code}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* User */}
            {userInfo ? (
              <Link
                className="flex cursor-pointer justify-center items-center gap-1.5 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors"
                to="/dashboard"
              >
                <span className="w-8 h-8 rounded-full overflow-hidden bg-gray-200 flex items-center justify-center shrink-0 border border-gray-200">
                  {profileImage ? (
                    <img src={profileImage} alt={userInfo.name} className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-xs font-bold text-gray-600">
                      {userInfo.name ? userInfo.name.charAt(0).toUpperCase() : <User size={14} />}
                    </span>
                  )}
                </span>
                <span className="max-w-[80px] truncate md-lg:hidden">{userInfo.name}</span>
              </Link>
            ) : (
              <Link
                to="/login"
                className="flex cursor-pointer justify-center items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900 transition-colors"
              >
                <Lock size={16} />
                <span className="md-lg:hidden">Login</span>
              </Link>
            )}

            {/* Wishlist */}
            <div
              onClick={() => navigate(userInfo ? "/dashboard/my-wishlist" : "/login")}
              className="relative flex justify-center items-center cursor-pointer w-[34px] h-[34px] md-lg:w-[30px] md-lg:h-[30px] rounded-full hover:bg-gray-100 transition-colors"
            >
              <Heart size={19} className="text-gray-600 md-lg:w-[17px] md-lg:h-[17px]" />
              {wishlist_count !== 0 && (
                <div className="w-[17px] h-[17px] md-lg:w-[15px] md-lg:h-[15px] absolute rounded-full text-[9px] md-lg:text-[8px] bg-red-500 text-white flex justify-center items-center -top-[3px] -right-[3px] md-lg:-top-[2px] md-lg:-right-[2px] font-medium">
                  {wishlist_count}
                </div>
              )}
            </div>

            {/* Cart */}
            <div
              onClick={redirect_cart_page}
              className="relative flex justify-center items-center cursor-pointer w-[34px] h-[34px] md-lg:w-[30px] md-lg:h-[30px] rounded-full hover:bg-gray-100 transition-colors"
            >
              <ShoppingBag size={19} className="text-gray-600 md-lg:w-[17px] md-lg:h-[17px]" />
              {card_product_count !== 0 && (
                <div className="w-[17px] h-[17px] md-lg:w-[15px] md-lg:h-[15px] absolute rounded-full text-white text-[9px] md-lg:text-[8px] bg-red-500 flex justify-center items-center -top-[3px] -right-[3px] md-lg:-top-[2px] md-lg:-right-[2px] font-medium">
                  {card_product_count}
                </div>
              )}
            </div>
          </div>

        </div>

        {/* ── Row 2 — Mobile-only: location chip + search bar ── */}
        <div className="hidden md-lg:flex items-center gap-2 mt-2.5">
          {/* Location chip */}
          <LocationPicker
            triggerClassName="h-[38px] border border-gray-200 rounded-lg bg-gray-50 px-2.5 hover:bg-gray-100"
          />

          {/* Clean search bar */}
          <div className="relative flex-1 min-w-0" ref={mobileSearchRef}>
            <div className={`flex items-center w-full border rounded-lg h-[38px] overflow-hidden bg-white shadow-[inset_0_1px_2px_rgba(0,0,0,0.04)] transition-colors ${
              showDropdown ? 'border-green-400 ring-1 ring-green-100' : 'border-gray-300'
            }`}>
              <Input
                className="border-none shadow-none focus-visible:ring-0 focus-visible:border-transparent w-full text-sm bg-transparent px-3 h-full"
                onChange={(e) => setSearchValue(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && search()}
                onFocus={handleSearchFocus}
                type="text"
                placeholder="Search products, brands..."
                value={searchValue}
              />
              <button
                onClick={search}
                className="h-full px-3.5 bg-[#00A651] hover:bg-[#009245] text-white shrink-0 transition-colors flex items-center"
              >
                <Search size={16} />
              </button>
            </div>
            <SearchDropdown
              visible={showDropdown}
              onClose={handleDropdownClose}
              searchValue={searchValue}
            />
          </div>
        </div>

      </div>
    </div>
  );
};

export default Headers;
